import { annotateSchedule } from "@/domain/prayer/detectCurrentPrayer";
import type { DaySchedule } from "@/domain/prayer/types";
import { cn } from "@/lib/cn";
import { formatIst } from "@/lib/time";

interface Props {
  schedule: DaySchedule;
  now: Date;
}

const DOT_CLASS: Record<string, string> = {
  completed: "bg-muted/60",
  current: "bg-masjidGreen ring-4 ring-masjidGreen/20",
  upcoming: "bg-line",
};

const STATUS_TEXT: Record<string, string> = {
  completed: "Completed",
  current: "Now",
  upcoming: "",
};

export function TimetableList({ schedule, now }: Props) {
  const rows = annotateSchedule(schedule, now);
  return (
    <ul className="divide-y divide-line overflow-hidden rounded-card border border-line bg-cream/60">
      {rows.map((r) => {
        const dim = r.status === "completed";
        return (
          <li
            key={r.name}
            className={cn(
              "px-4 py-4 sm:px-5",
              r.status === "current" && "bg-masjidGreen/5",
            )}
          >
            <div className="flex items-baseline justify-between gap-3">
              <div className="flex items-center gap-3">
                <span
                  aria-hidden
                  className={cn(
                    "h-2.5 w-2.5 shrink-0 rounded-full",
                    DOT_CLASS[r.status],
                  )}
                />
                <span
                  className={cn(
                    "font-serif text-lg sm:text-xl",
                    dim ? "text-muted" : "text-ink",
                  )}
                >
                  {r.label}
                </span>
              </div>
              {STATUS_TEXT[r.status] ? (
                <span className="text-[10px] uppercase tracking-widest text-masjidGreen/80">
                  {STATUS_TEXT[r.status]}
                </span>
              ) : null}
            </div>
            <dl className="mt-2 ml-[1.375rem] grid grid-cols-[auto_1fr] gap-x-4 gap-y-0.5 text-sm sm:ml-7">
              <dt className="text-muted">Azaan</dt>
              <dd
                className={cn(
                  "text-right font-serif tabular-nums",
                  dim ? "text-muted" : "text-ink",
                )}
              >
                {formatIst(r.azaanTime, "h:mm a")}
              </dd>
              <dt className="text-muted">Jama&rsquo;ah</dt>
              <dd
                className={cn(
                  "text-right font-serif tabular-nums",
                  dim ? "text-muted" : "text-ink",
                )}
              >
                {formatIst(r.jamaahTime, "h:mm a")}
              </dd>
            </dl>
          </li>
        );
      })}
    </ul>
  );
}
